// React and third-party libraries
import * as React from "react";
import styled from "styled-components";
import { motion } from 'framer-motion';
import { ArrowRight } from "lucide-react";
import { Link } from "gatsby";

// Local imports (styles, constants, etc.)
import { colors, media } from "../../../style/constants";
import { sectionPadding } from "../../../style/layout";

// Translation
import { useTranslation } from "react-i18next";
import { GatsbyImage } from "gatsby-plugin-image";

/* Styled Components */

// Section wrapper with vertical padding and overflow handling
const SectionWrapper = styled.section`
  position: relative;
  overflow: hidden;
  ${sectionPadding}
`;

// Central container with max width and horizontal centering
const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  position: relative;
`;

// Wrapper for the top title and subtitle content
const TextContent = styled(motion.div)`
  margin: 0 auto 2rem;
  max-width: 48rem;

  @media (min-width: ${media.tabletSm}px) {
    margin: 0 auto 4rem;
  }
`;

// Main section heading
const Heading = styled(motion.h2)`
  text-align: center;
  margin-bottom: 1rem;
  font-size: 1.75rem;
  line-height: 2.25rem;

  @media (min-width: ${media.tabletSm}px) {
    font-size: 1.875rem;
    line-height: 2.25rem;
  }
`;

// Paragraph under the heading
const Paragraph = styled(motion.p)`
  font-size: 1.125rem;
  line-height: 1.5;

  @media (min-width: ${media.tabletSm}px) {
    font-size: 1.2rem;
    line-height: 1.5;
  }
`;

// Grid layout for the hardware cards
const GridLayout = styled(motion.div)`
  display: grid;
  gap: 1rem;
  grid-template-columns: repeat(1, minmax(0, 1fr));
  justify-content: center;
  
  @media (min-width: ${media.tabletLg}px) {
    gap: 2rem;
    grid-template-columns: repeat(auto-fit, minmax(0, 378px));
  }
`;

// Each individual card
const CardContainer = styled(motion.div)`
  background: ${colors.card.default};
  border: 1px solid ${colors.border.default};
  padding: 1.5rem;
  border-radius: 12px;
  text-align: center;
  transform: translateY(20px);
  transition: border 0.6s ease, transform 0.3s ease;

  &:hover {
    border: 1px solid rgba(42, 163, 223, 0.4);
  }
`;

// Image header block in the card
const CardHeader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 12rem;
  margin-bottom: 1rem;
  border-radius: 3px;
  background-color: rgb(12, 18, 24, 0.2);

  @media (min-width: ${media.tabletLg}px) {
    height: 15rem;
  }
`;

// Body section of the card
const CardBody = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.5px;

  @media (min-width: ${media.tabletLg}px) {
    gap: 0.625rem;
  }
`;

// Card title
const Subheading = styled.h3`
  text-align: left;
  font-size: 1.25rem;
  line-height: 1.75rem;
  font-weight: 500;
  margin-bottom: 0.5rem;
`;

// Card description
const Text = styled.p`
  text-align: left;
  color: ${colors.lightGrey};
  font-size: 1rem;
  margin-bottom: 0.5rem;
`;

// Icon wrapper for link hover animation
const IconContainer = styled(motion.div)`
  display: inline-flex;
  transition: transform 0.3s ease;
`;

// Styled Gatsby link with hover animation
const StyledLink = styled(Link)`
  text-align: left;
  color: ${colors.primary};
  display: flex;
  align-items: center;
  font-size: 0.875rem;
  gap: 4px;
  margin-top: auto;

  &:hover {
    ${IconContainer} {
      transform: translateX(4px);
    }
  }
`;


/**
 * HardwareSection Component - Products Page
 * Displays a grid of available hardware devices
 * 
 * @param {string} title - Section title
 * @param {string} subtitle - Section description
 * @param {Array} hardwareList - Array of hardware items with title, subtitle, link, image
 */
const HardwareSection = ({
  title,
  subtitle,
  hardwareList
}) => {
  const { t } = useTranslation(); // Explicitly specify the namespace

  return (
    <SectionWrapper id="hardwareSection">
      <Container>
        {/* Section header */}
        <TextContent
          variants={{
            hidden: { opacity: 0, y: 20 },
            show: { opacity: 1, y: 0, transition: { duration: 0.6 } }
          }}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: "All" }}
        >
          <Heading
            variants={{
              hidden: { opacity: 0, y: 20 },
              show: { opacity: 1, y: 0, transition: { duration: 0.6 } }
            }}
          >
            {title}
          </Heading>
          <Paragraph
            variants={{
              hidden: { opacity: 0, y: 20 },
              show: { opacity: 1, y: 0, transition: { duration: 0.6, delay: 0.2 } }
            }}
          >
            {subtitle}
          </Paragraph>
        </TextContent>

        {/* Grid of hardware cards */}
        <GridLayout
          variants={{
            hidden: { opacity: 0 },
            show: { opacity: 1, transition: { staggerChildren: 0.25 } }
          }}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
        >
          {hardwareList?.map((hardware, index) => (
            <CardContainer
              key={`hardware-${index}`}
              variants={{
                hidden: { opacity: 0 },
                show: { opacity: 1 }
              }}
            >
              <CardHeader>
                <GatsbyImage
                  image={hardware.imageData} 
                  alt={hardware.imgAlt} 
                  style={{ 
                    transform: "rotate(15deg)",
                    width: "180px",
                  }} 
                />
              </CardHeader>
              <CardBody>
                <Subheading>{hardware.title}</Subheading>
                <Text>{hardware.subtitle}</Text>
                <StyledLink to={hardware.link} aria-label={`${t("callToAction.learnMoreAbout")} ${hardware.title}`}>
                  {t("hardwareSection.learnMore")} <IconContainer><ArrowRight size={16} /></IconContainer>
                </StyledLink>
              </CardBody>
            </CardContainer>
          ))}
        </GridLayout>
      </Container>
    </SectionWrapper>
  );
};

export default HardwareSection;
